import './sentry'; // MUST be first — install error handlers before anything renders
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { isNative, configureStatusBar } from '@ah/native/runtime';
import { markBundleHealthy } from '@ah/native/update';
import { ensureDefaultNotificationChannel } from '@ah/native/push';
import '@ah/design/styles';
import '@ah/design/theme.css';
import './styles.css';
import { App } from './App';
import { captureSessionToken } from './accounts';
import { registerPwaUpdates } from './pwa-update';
captureSessionToken();

if (isNative()) {
  // Tag the <html> element so CSS can opt out of PWA-only layout rules
  // that don't apply inside Capacitor's WKWebView (e.g. forcing 100vh).
  // Set before React mounts so the first paint already has the right
  // height — otherwise the layout reflows mid-load.
  document.documentElement.classList.add('capacitor');

  // Capacitor 8 made the Android WebView edge-to-edge by default but does
  // not (yet) forward WindowInsets into CSS env(safe-area-inset-*) values,
  // so the React header overlaps the system status bar (clock / battery).
  // Disable WebView overlay so the status bar reserves its own space.
  // iOS no-op. See @ah/native/runtime configureStatusBar.
  void configureStatusBar();

  // Create the default notification channel on Android. Required by
  // Android 8+ for any pushed notification to actually surface as a banner.
  // Idempotent + no-op on iOS. See @ah/native/push.
  void ensureDefaultNotificationChannel();

  // Note: the foreground-update check (manifest poll + download + apply)
  // is handled by <UpdateGate /> after React mounts. We used to do a
  // boot-time stage-for-next-launch here too, but doing both racks two
  // downloads against each other for the same release.
} else {
  // Web-only: workbox / vite-plugin-pwa update flow. Inside Capacitor the
  // service worker is bypassed by the OTA plugin, so registering it would
  // just confuse cache invalidation.
  registerPwaUpdates();
}

// Tell the OTA plugin this bundle booted successfully. Called unconditionally
// because `isNative()` (above) can race Capacitor's bridge injection on
// Android — if the bridge isn't wired at module-eval time, the gate above
// returns false and we'd skip the heartbeat entirely, causing Capgo to
// roll back this bundle after its 30s timeout. markBundleHealthy() internally
// polls for the bridge for 5s and gives up cleanly on real web, so the
// unconditional call is safe across all three platforms.
void markBundleHealthy();

// Lock orientation to portrait when running as an installed PWA. The manifest
// already declares this but Chrome/Android only enforce the JS API call.
if (window.matchMedia('(display-mode: standalone)').matches
  || window.matchMedia('(display-mode: fullscreen)').matches) {
  const orientation = (screen as Screen & { orientation?: ScreenOrientation }).orientation;
  const lock = orientation as (ScreenOrientation & { lock?: (o: string) => Promise<void> }) | undefined;
  lock?.lock?.('portrait').catch(() => {});
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

function FallbackError({ resetError }: { resetError: () => void }) {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 bg-background p-6 text-center">
      <h1 className="text-xl font-semibold">Something went wrong</h1>
      <p className="text-sm opacity-70">We&apos;ve been notified. Try reloading.</p>
      <button
        className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        onClick={() => { resetError(); window.location.reload(); }}
      >
        Reload
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={FallbackError} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
